import React, { useState, useRef, useEffect } from 'react'
import { useDragText } from "../../../utils/useDragText"

const TextBlock = ({ block, updateTextBlock, removeTextBlock, isSelected, onSelect }) => {
    const blockRef = useRef(null)
    const [isResizing, setIsResizing] = useState(false)
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0 })

    const getCurrentDimensions = () => {
        if (!blockRef.current) return { width: 200, height: 'auto' }

        return {
            width: typeof block.width === 'number' ? block.width : blockRef.current.offsetWidth,
            height: typeof block.height === 'number' ? block.height : blockRef.current.offsetHeight
        }
    }

    const { position, handleMouseDown } = useDragText(
        { x: block.x, y: block.y },
        (newPosition) => updateTextBlock(block.id, newPosition)
    )

    const handleDelete = (e) => {
        e.stopPropagation()
        removeTextBlock(block.id)
    }

    const handleResizeMouseDown = (e) => {
        e.stopPropagation()
        setIsResizing(true)
        const currentDimensions = getCurrentDimensions()
        setResizeStart({
            x: e.clientX,
            y: e.clientY,
            startWidth: currentDimensions.width,
            startHeight: currentDimensions.height
        })
    }

    const handleBlockClick = (e) => {
        e.stopPropagation()
        onSelect(block.id)
    }

    useEffect(() => {
        if (!isResizing) return

        const handleMouseMove = (e) => {
            const deltaX = e.clientX - resizeStart.x
            const deltaY = e.clientY - resizeStart.y

            const newWidth = Math.max(50, resizeStart.startWidth + deltaX)
            const newHeight = Math.max(20, resizeStart.startHeight + deltaY)

            updateTextBlock(block.id, {
                width: newWidth,
                height: newHeight
            })
        }

        const handleMouseUp = () => setIsResizing(false)

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isResizing, resizeStart, block.id, updateTextBlock])

    const blockStyle = {
        left: `${position.x}px`,
        top: `${position.y}px`,
        color: block.color,
        fontSize: `${block.fontSize}px`,
        fontFamily: block.fontFamily,
        fontWeight: block.fontWeight,
        fontStyle: block.fontWeight === 'italic' ? 'italic' : 'normal',
        textAlign: block.textAlign || 'left',
        position: 'absolute',
        ...(typeof block.width === 'number' && { width: `${block.width}px` }),
        ...(typeof block.height === 'number' && { height: `${block.height}px` }),
        zIndex: isSelected ? 10 : 1,
        boxShadow: isSelected ? '0 0 0 2px #1890ff' : 'none',
        background: isSelected ? 'rgba(24, 144, 255, 0.1)' : 'transparent',
        border: isSelected ? '1px dashed #1890ff' : 'none',
        padding: isSelected ? '4px' : '2px',
        borderRadius: isSelected ? '4px' : '0',
        cursor: 'move',
        userSelect: 'none',
        whiteSpace: block.width && block.width !== 'auto' ? 'normal' : 'nowrap',
        wordWrap: block.width && block.width !== 'auto' ? 'break-word' : 'normal',
    }

    return (
        <div
            ref={blockRef}
            className={`TextBlock ${isSelected ? 'selected' : ''}`}
            style={blockStyle}
            onClick={handleBlockClick}
            id={block.key}
            onMouseDown={(e) => {
                handleMouseDown(e)
                handleBlockClick(e)
            }}
        >
            {block.text}
            {block.key && (
                <div style={{
                    position: 'absolute',
                    top: -20,
                    left: 0,
                    fontSize: '10px',
                    background: '#1890ff',
                    color: 'white',
                    padding: '1px 4px',
                    borderRadius: '2px',
                    whiteSpace: 'nowrap'
                }}>
                    Ключ: {block.key}
                </div>
            )}

            <button
                className="TextBlock-button button-delete"
                onClick={handleDelete}
                style={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#ff4d4f',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 100
                }}
            >
                ×
            </button>

            {isSelected && (
                <div
                    className="TextBlock-button resize-handle"
                    onMouseDown={handleResizeMouseDown}
                    style={{
                        position: 'absolute',
                        bottom: -4,
                        right: -4,
                        width: '12px',
                        height: '12px',
                        background: '#1890ff',
                        borderRadius: '2px',
                        cursor: 'se-resize',
                        zIndex: 100
                    }}
                />
            )}
        </div>
    )
}

export default TextBlock